/**
 * Payment Success Dialog Component
 *
 * Shows a confirmation dialog when payment is completed successfully.
 * Automatically closes and clears URL params after display.
 */
import { CheckCircle2Icon } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router";

export function PaymentSuccessDialog() {
  const [searchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [credits, setCredits] = useState<number | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Check for payment success params
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    const creditsParam = searchParams.get("credits");

    if (paymentStatus === "success") {
      setCredits(creditsParam ? parseInt(creditsParam, 10) : null);
      setOpen(true);

      // Clear URL params without navigation
      const url = new URL(window.location.href);
      url.searchParams.delete("payment");
      url.searchParams.delete("credits");
      window.history.replaceState({}, "", url.pathname);
    }
  }, [searchParams]);

  const handleClose = () => {
    setOpen(false);
  };

  if (!open || !mounted) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative flex w-full max-w-xs animate-[fadeInUp_0.3s_ease-out] flex-col items-center overflow-hidden rounded-3xl bg-white p-8 shadow-2xl">
        {/* Success Icon */}
        <div className="mb-5 flex size-16 items-center justify-center rounded-full bg-green-100">
          <CheckCircle2Icon className="size-8 text-green-600" />
        </div>

        {/* Title */}
        <h3 className="mb-2 text-xl font-black tracking-tight text-black">
          충전 완료!
        </h3>

        {/* Credits info */}
        {credits && (
          <p className="mb-1 text-3xl font-black text-black">
            +{credits} <span className="text-lg font-bold">크레딧</span>
          </p>
        )}

        <p className="mb-6 text-center text-sm text-gray-500">
          크레딧이 성공적으로 충전되었습니다.
        </p>

        {/* Close button */}
        <button
          onClick={handleClose}
          className="w-full rounded-xl bg-black py-3 text-sm font-bold text-white transition-colors hover:bg-gray-800"
        >
          확인
        </button>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
